import React, { PureComponent } from "react";
import PlaylistItem from "../components/PlaylistItem";
import styled from "styled-components";

export interface Props {
  episodes: any;
  currentEpisode: number;
  maxVisibleItems: number;
  customColors?: any;
  selectPlaylistItem: (...args: any[]) => void;
}

/**
 * Playlist component
 */

export default class Playlist extends PureComponent<any, any> {
  private playlistElement: HTMLDivElement;
  private playlistItem: PlaylistItem;

  constructor(props: any) {
    super(props);

    this.state = {
      blurTop: false,
      blurBottom: props.maxVisibleItems < props.episodes.length,
      maxScrollLength: props.episodes.length - props.maxVisibleItems,
      playlistItemHeight: 0,
      hascolors: this.props.customColors.length > 0 ? true : false
    };
  }

  componentDidMount() {
    window.requestAnimationFrame(() => {
      if (this.playlistItem.getPlayListItem() !== undefined) {
        this.setState({
          playlistItemHeight: this.playlistItem
            .getPlayListItem()
            .getBoundingClientRect().height
        });
      }

      if (this.playlistElement !== undefined) {
        this.setState({ scrollBarWidth: this.getScrollbarWidth() });
        this.playlistElement.addEventListener(
          "scroll",
          this.determineBlurClassState
        );
      }
    });
  }

  componentWillUnmount() {
    this.playlistElement.removeEventListener(
      "scroll",
      this.determineBlurClassState
    );
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentEpisode !== prevProps.currentEpisode) {
      this.scrollCurrentEpisodeIntoView();
    }
  }

  getScrollbarWidth() {
    return window.innerWidth - document.documentElement.clientWidth;
  }

  /**
   * Auto scrolls the playlist when the item that is currently playing goes out of view.
   */
  scrollCurrentEpisodeIntoView = () => {
    const playlistClientRect = this.playlistElement.getBoundingClientRect();
    const isCurrentClientRect = (document.querySelector(
      ".isCurrent"
    ) as HTMLElement).getBoundingClientRect();
    this.handlePlayListScroll(playlistClientRect, isCurrentClientRect);
  };

  handlePlayListScroll = (playlistClientRect, isCurrentClientRect) => {
    if (this.props.currentEpisode <= 1) {
      this.playlistElement.scrollTop = 0;
    } else if (
      isCurrentClientRect.bottom > playlistClientRect.top &&
      isCurrentClientRect.bottom > playlistClientRect.bottom
    ) {
      this.playlistElement.scrollTop =
        isCurrentClientRect.height * this.props.currentEpisode;
    } else if (
      isCurrentClientRect.top < playlistClientRect.bottom &&
      isCurrentClientRect.top < playlistClientRect.top
    ) {
      this.playlistElement.scrollTop =
        this.playlistElement.scrollTop -
        isCurrentClientRect.height * this.props.maxVisibleItems;
    }
  };

  determineBlurClassState = (e: Event) => {
    const target = e.target as HTMLDivElement;
    target.scrollTop >= this.state.playlistItemHeight / 2
      ? this.setState({ blurTop: true })
      : this.setState({ blurTop: false });
    target.scrollTop >=
    this.state.maxScrollLength * this.state.playlistItemHeight -
      this.state.playlistItemHeight / 2
      ? this.setState({ blurBottom: false })
      : this.setState({ blurBottom: true });
  };

  select = (index: number) => {
    this.props.selectPlaylistItem(index);
  };

  render() {
    const blurTop = this.state.blurTop ? "blur-top-active" : "";
    const blurBottom = this.state.blurBottom ? "blur-bottom-active" : "";

    const playListItems: any[] = [];
    for (let i = 0; i < this.props.episodes.length; i++) {
      playListItems.push(
        <PlaylistItem
          title={this.props.episodes[i].title}
          duration={this.props.episodes[i].media[1].duration}
          playlistItemIndex={i}
          currentEpisode={this.props.currentEpisode}
          select={this.select}
          key={i}
          ref={ref => (this.playlistItem = ref!)}
          customColors={this.props.customColors}
        />
      );
    }
    return (
      <PlayListWrapper
        className={`playlist-wrapper blur-top ${blurTop} blur-bottom ${blurBottom}`}
        customColors={this.state.hascolors ? this.props.customColors : null}
      >
        <PlayList
          ref={ref => (this.playlistElement = ref)}
          className="playlist"
          style={{
            height: `${this.props.maxVisibleItems *
              this.state.playlistItemHeight}px`
          }}
          customColors={this.state.hascolors ? this.props.customColors : null}
        >
          {playListItems}
        </PlayList>
      </PlayListWrapper>
    );
  }
}

const PlayListWrapper: any = styled.div`
  margin: 0;
  overflow: hidden;

  &[class*="blur"] {
    position: relative;
    &:after,
    &:before {
      position: absolute;
      content: "";
      width: 100%;
      height: 20px;
      pointer-events: none;
    }
  }
  &.blur-top {
    &:after {
      opacity: 0;
      transition: opacity 0.1s ease-in-out;
      display: none;
    }
    &.blur-top-active {
      &:after {
        opacity: 1;
        transition: opacity 0.1s ease-in-out;
        z-index: 1;
        display: block;
      }
    }
    &:after {
      top: 0;
      background: -moz-linear-gradient(
        top,
        ${(props: any) => (props.customColors ? props.customColors[5] : "#000")}
          0%,
        transparent 100%
      ); /* FF3.6-15 */
      background: -webkit-linear-gradient(
        top,
        ${(props: any) => (props.customColors ? props.customColors[5] : "#000")}
          0%,
        transparent 100%
      ); /* Chrome10-25,Safari5.1-6 */
      background: linear-gradient(
        to bottom,
        ${(props: any) => (props.customColors ? props.customColors[5] : "#000")}
          0%,
        transparent 100%
      ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
    }
  }
  &.blur-bottom {
    &:before {
      opacity: 0;
      transition: opacity 0.1s ease-in-out;
      display: none;
    }
    &.blur-bottom-active {
      &:before {
        opacity: 1;
        transition: opacity 0.1s ease-in-out;
        z-index: 1;
        display: block;
      }
    }
    &:before {
      bottom: 0;
      background: -moz-linear-gradient(
        top,
        transparent 0%,
        ${(props: any) => (props.customColors ? props.customColors[5] : "#000")}
          100%
      ); /* FF3.6-15 */
      background: -webkit-linear-gradient(
        top,
        transparent 0%,
        ${(props: any) => (props.customColors ? props.customColors[5] : "#000")}
          100%
      ); /* Chrome10-25,Safari5.1-6 */
      background: linear-gradient(
        to bottom,
        transparent 0%,
        ${(props: any) => (props.customColors ? props.customColors[5] : "#000")}
          100%
      ); /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
    }
  }
`;

const PlayList: any = styled.div`
  position: relative;
  width: 100%;
  background-color: ${(props: any) =>
    props.customColors ? props.customColors[5] : "#000"};
  color: #fff;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-direction: column;
  flex-direction: column;
  margin: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  width: 9999px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  padding-right: 100px;
  &::-webkit-scrollbar {
    width: 0px;
  }
`;
